import 'es6-promise';
import { ApiEvents, CustomEvent } from './ApiEvents';

interface TaskObjectMessage {
	task: string;
	arguments: {};
	id: string;
	type: string;
	responseType: string;
	responseValue: string;
	resolve: null;
	reject: null;
}

interface TaskObject {
	task: string;
	arguments: {};
	id: string;
	type: string;
	responseType: string;
	responseValue: string;
	resolve: TaskResolve;
	reject: TaskReject;
}

export enum PostMessageTarget {
	GallApp = 'GallApp'
}

export enum PostMessageAction {
	Authentication = 'authentication',
	GoTo = 'goTo',
	OpenDriveItem = 'openDriveItem'
}

type TaskResolve = (value?: {} | PromiseLike<{}> | undefined) => void;
type TaskReject = (reason?: string) => void;

class ReaderApi {

	private listeners: ((data: {}) => void)[];
	private listenersBind: ((event: CustomEvent) => void)[];
	private tasks: TaskObject[];
	private ApiReadyResolve: TaskResolve;
	private ApiReadyReject: TaskReject;
	private apiEvents: ApiEvents;

	constructor() {
		this.listeners = [];
		this.listenersBind = [];
		this.tasks = [];
		this.actionReceiver = this.actionReceiver.bind(this);
		this.apiEvents = new ApiEvents();
	}

	public init(): Promise<{}> {
		let promise = new Promise((resolve, reject) => {
			this.ApiReadyResolve = resolve;
			this.ApiReadyReject = reject;
		});

		this.addListener('getConnection', (data: TaskObject) => {
			data.resolve();
			this.ApiReadyResolve(data);
		});

		this.requestConnection();
		this.addMessageListeners();
		return promise;
	}

	public call(task: string, args: {}): Promise<{}> {
		let taskObjectMessage: TaskObjectMessage, taskObject: TaskObject, promise: Promise<{}>;

		taskObjectMessage = {
			task: task,
			arguments: args,
			id: getGlobalTaskID(),
			type: 'call',
			responseType: 'resolve',
			responseValue: '',
			resolve: null,
			reject: null,
		};

		promise = new Promise((resolve, reject) => {
			taskObject = {
				...taskObjectMessage,
				resolve: resolve,
				reject: reject,
			};
			this.tasks.push(taskObject);
		});

		this.postMessage(taskObjectMessage);
		return promise;
	}

	public addListener(name: string, callBack: (data: TaskObject) => void): void {
		let _callBack = (event: CustomEvent): void => {
			callBack(event.data as TaskObject);
		};

		this.listeners.push(callBack);
		this.listenersBind.push(_callBack);

		this.apiEvents.addListener(name, _callBack);
	}

	public removeListener(name: string, callBack: (data: TaskObject) => void): void {
		let listeners = this.listeners;
		let listenersBind = this.listenersBind;

		this.listeners = [];
		this.listenersBind = [];

		for (let i = 0, len = listeners.length; i < len; i++) {
			if (listeners[i] === callBack) {
				this.apiEvents.removeListener(name, listenersBind[i]);
				continue;
			}

			this.listeners.push(listeners[i]);
			this.listenersBind.push(listenersBind[i]);
		}

	}

	public flush(): void {
		this.removeMessageListeners();
	}

	private responseCall(taskObject: TaskObject): void {
		this.postMessage({
			...taskObject,
			resolve: null,
			reject: null,
			type: 'response'
		} as TaskObjectMessage);
	}

	private dispatchEvent(data: TaskObject): void {
		this.apiEvents.fire({
			type: data.task, data: {
				arguments: data.arguments,
				resolve: data.resolve,
				reject: data.reject
			}
		});
	}

	private postMessage(taskObject: TaskObjectMessage): void {
		if (isSameWindow(window, window.parent) === true) {
			this.ApiReadyReject('there is no child or parent window');
			return;
		}
		window.parent.postMessage(taskObject, '*');
	}

	private requestConnection(): void {
		this.call('getConnection', 'Reader try to make connection with backoffice').then((data: TaskObjectMessage) => {
			this.ApiReadyResolve(data);
		});
	}

	private addMessageListeners(): void {
		window.addEventListener('message', this.actionReceiver, false);
	}

	private removeMessageListeners(): void {
		window.removeEventListener('message', this.actionReceiver, false);
	}

	private actionReceiver(event: MessageEvent): void {
		let tasksLength: number, promise: Promise<{}>, data: TaskObject;

		if (!isSameWindow(window.parent, event.source) || (event.data instanceof Object) === false) {
			return;
		}

		data = <TaskObject> event.data;

		tasksLength = this.tasks.length;

		this.tasks = this.tasks.filter((taskObject: TaskObject): TaskObject | undefined => {

			if (taskObject.id !== data.id) {
				return taskObject;
			}

			if (data.responseType === 'resolve') {
				taskObject.resolve(data.arguments);
			} else {
				taskObject.reject(data.responseValue);
			}

			return undefined;
		});


		// true, means no change in tasks and it's called from Reader
		if (tasksLength === this.tasks.length) {
			if (data.type === 'response') {
				return;
			}

			promise = new Promise((resolve, reject) => {
				data.resolve = resolve;
				data.reject = reject;
			});

			promise.then((e) => {
				data.arguments = e;
				data.responseType = 'resolve';
				data.responseValue = '';
				this.responseCall(data);
			}).catch((e) => {
				data.arguments = [];
				data.responseType = 'reject';
				data.responseValue = e;
				this.responseCall(data);
			});

			this.dispatchEvent(data);
		}
	}
}

const getGlobalTaskID: () => string = function(): () => string {
	let id = 0;
	return (): string => {
		return 'reader-' + (id++) + Date.now();
	};
}();

// Utils
function isSameWindow(registeredWindow: Window, messageSource: Window|null): boolean {
	return registeredWindow === messageSource;
}

export default ReaderApi;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/iframeApi/ReaderApi.ts